import {
    type components,
    type operations,
} from '@libraries/typescript/api_client/generated_api_types';
import { type AreTypesEqual, assert } from '@libraries/typescript/utilities';

export type Message = components['schemas']['MessageRead'];
export type ManualPhoneContact =
    components['schemas']['ManualPhoneContactRead'];
export type User = components['schemas']['UserRead'];
export type ChatUser = components['schemas']['ChatUserRead'];
export type CaregiverRole = components['schemas']['CaregiverRole'];
export type OrgMemberRole = components['schemas']['OrgMemberRole'];
export type CustomerLifecycleStage =
    components['schemas']['CustomerLifecycleStage'];
export type AgentChecklistState =
    components['schemas']['AgentChecklistStateRead'];
export type CaregiverPreferences =
    components['schemas']['CaregiverPreferences'];
export type UserEmailNotificationPreferences =
    components['schemas']['UserEmailNotificationPreferences'];
export type NotificationChannelPreference =
    components['schemas']['NotificationChannelPreference'];
export type Conversation = components['schemas']['ConversationRead'];
export type ConversationProcessingState =
    components['schemas']['ConversationProcessingState'];
export type ConversationStatus = components['schemas']['ConversationStatus'];
export type ConversationAuditEventType =
    components['schemas']['ConversationAuditEventType'];
export type OrganizationConversationMetadata =
    components['schemas']['OrganizationConversationMetadata'];
export type OrganizationConversation =
    components['schemas']['OrganizationConversationRead'];
export type OrganizationConversationAuditEvent =
    components['schemas']['OrganizationConversationAuditEvent'];
export type UpdateConversationAssignmentRequest =
    components['schemas']['UpdateConversationAssignmentRequest'];
export type ConversationAssignmentResponse =
    components['schemas']['ConversationAssignmentResponse'];
export type ConversationMessageNoteRead =
    components['schemas']['ConversationMessageNoteRead'];
export type ConversationMessageNotesResponse =
    components['schemas']['ConversationMessageNotesResponse'];
export type ConversationLifecycleResponse =
    components['schemas']['ConversationLifecycleResponse'];
export type ConversationAutomatedReplyPauseResponse =
    components['schemas']['ConversationAutomatedReplyPauseResponse'];
export type UpdateConversationAutomatedReplyPauseRequest =
    components['schemas']['UpdateConversationAutomatedReplyPauseRequest'];
export type OrganizationConversationCaregiverStatus =
    components['schemas']['OrganizationConversationCaregiverStatus'];
export type ProactiveSlackMessageRequest =
    components['schemas']['ProactiveSlackMessageRequest'];
export type ProactiveSlackMessageResponse =
    components['schemas']['ProactiveSlackMessageResponse'];
export type AdminFlag =
    operations['get_admin_flags_admin_admin_flags_get']['responses'][200]['content']['application/json'][number];
export type PromptRolloutResponse =
    components['schemas']['PromptRolloutResponse'];
export type PromptRolloutEntry = components['schemas']['PromptRolloutEntry'];
export type PromotePromptRequest =
    components['schemas']['PromotePromptRequest'];
export type PromotePromptResponse =
    components['schemas']['PromotePromptResponse'];
export type AdminSourceHealthEntry =
    components['schemas']['AdminSourceHealthEntry'];
export type AdminSourceEnvironmentHealth =
    components['schemas']['AdminSourceEnvironmentHealth'];
export type AdminSourceHealthResponse =
    components['schemas']['AdminSourceHealthResponse'];
export type AdminAgentMissingToolReportRead =
    components['schemas']['AdminAgentMissingToolReportRead'];
export type AdminAgentMissingToolCapabilitySummary =
    components['schemas']['AdminAgentMissingToolCapabilitySummary'];
export type AdminAgentMissingToolListResponse =
    components['schemas']['AdminAgentMissingToolListResponse'];
export type AdminSavedInvestigationRead =
    components['schemas']['AdminSavedInvestigationRead'];
export type AdminSavedInvestigationCreateRequest =
    components['schemas']['AdminSavedInvestigationCreateRequest'];
export type AdminSavedInvestigationListResponse =
    components['schemas']['AdminSavedInvestigationListResponse'];
export type AdminSavedInvestigationTerminalState =
    components['schemas']['AdminSavedInvestigationTerminalState'];
export type AdminSavedInvestigationConfidence =
    components['schemas']['AdminSavedInvestigationConfidence'];
export type AdminImessageThreadRead =
    components['schemas']['AdminImessageThreadRead'];
export type AdminImessageListItemRead =
    components['schemas']['AdminImessageListItemRead'];
export type AdminImessageThreadMessageRead =
    components['schemas']['AdminImessageThreadMessageRead'];
export type AdminImessageThreadListResponse =
    components['schemas']['AdminImessageThreadListResponse'];
export type AdminImessageThreadDetailResponse =
    components['schemas']['AdminImessageThreadDetailResponse'];
export type AdminImessageThreadUnbindRunResponse =
    components['schemas']['AdminImessageThreadUnbindRunResponse'];
export type AdminImessageFilterOptionRead =
    components['schemas']['AdminImessageFilterOptionRead'];
export type AdminImessageFilterOptionsResponse =
    components['schemas']['AdminImessageFilterOptionsResponse'];
export type AdminImessageRequestCatalogItemRead =
    components['schemas']['AdminImessageRequestCatalogItemRead'];
export type AdminImessageRequestCatalogResponse =
    components['schemas']['AdminImessageRequestCatalogResponse'];
export type AdminImessageRequestExampleRead =
    components['schemas']['AdminImessageRequestExampleRead'];
export type AdminImessageSourceNumberRead =
    components['schemas']['AdminImessageSourceNumberRead'];
export type AdminImessageSourceNumbersResponse =
    components['schemas']['AdminImessageSourceNumbersResponse'];
export type AdminImessageStaffDiagnosticsResponse =
    components['schemas']['AdminImessageStaffDiagnosticsResponse'];
export type AdminImessageStaffFlagRead =
    components['schemas']['AdminImessageStaffFlagRead'];
export type AdminImessageStaffBudgetRead =
    components['schemas']['AdminImessageStaffBudgetRead'];
export type AdminImessageProvisionRequest =
    components['schemas']['AdminImessageProvisionRequest'];
export type AdminImessageProvisionResponse =
    components['schemas']['AdminImessageProvisionResponse'];
export type InlineReviewOutcome =
    | 'pending'
    | 'accepted'
    | 'rewrite_applied'
    | 'rewrite_no_op'
    | 'rewrite_failed'
    | 'rewrite_skipped'
    | 'preview_accepted'
    | 'preview_would_rewrite'
    | 'preview_rewrite_failed'
    | 'preview_rewrite_no_op'
    | 'preview_would_disable_auto_send'
    | 'error';
export type InlineReviewIssueBucket =
    | 'intent_understanding'
    | 'action_selection'
    | 'reply_accuracy'
    | 'auto_send_safety'
    | 'language_or_tone'
    | 'workflow_policy'
    | 'context_gap'
    | 'other';
export type AdminShift = components['schemas']['AdminShiftRead'];
export type AdminShiftListResponse =
    components['schemas']['AdminShiftListResponse'];
export type AdminOrgCaregiverRecord =
    components['schemas']['AdminOrgCaregiverRecordRead'];
export type AdminOrgCaregiverRecordListResponse =
    components['schemas']['AdminOrgCaregiverRecordListResponse'];
export type AdminCaregiverFiltersResponse =
    components['schemas']['AdminCaregiverFiltersResponse'];
export type AdminCareCoordinator =
    components['schemas']['AdminCareCoordinatorRead'];
export type AdminCareCoordinatorListResponse =
    components['schemas']['AdminCareCoordinatorListResponse'];
export type AdminClient = components['schemas']['AdminClientRead'];
export type AdminClientListResponse =
    components['schemas']['AdminClientListResponse'];
export type AdminClientCreate = components['schemas']['AdminClientCreate'];
export type AdminClientUpdate = components['schemas']['AdminClientUpdate'];
export type AdminClientOfficesResponse =
    components['schemas']['OfficesListResponse'];
export type AdminClientOffice = components['schemas']['OfficeRead'];
export type OrganizationOfficesResponse =
    components['schemas']['OfficesResponse'];
export type OrganizationOffice = components['schemas']['OfficeResponse'];
export type OfficeConversationCountsResponse =
    components['schemas']['OfficeConversationCountsResponse'];
export type OfficeConversationCount =
    components['schemas']['OfficeConversationCount'];
export type AdminOrganizationListResponse =
    components['schemas']['GetOrganizationsAdminResponse'];
export type AdminUserListResponse =
    components['schemas']['AdminUserListResponse'];
export type AdminUserRead = components['schemas']['AdminUserRead'];
export type AdminRecentRunItem = components['schemas']['AdminRecentRunItem'];
export type AdminRecentRunsResponse =
    components['schemas']['AdminRecentRunsResponse'];
export type CoordinatorPresenceResponse =
    components['schemas']['CoordinatorPresenceResponse'];
export type CoordinatorBrowserAvailabilityRequest =
    components['schemas']['CoordinatorBrowserAvailabilityRequest'];
export type AdminCoordinatorPresenceResponse =
    components['schemas']['AdminCoordinatorPresenceResponse'];
export type TestIncomingCallRequest =
    components['schemas']['TestIncomingCallRequest'];
export type IncomingCallData = components['schemas']['IncomingCallData'];
export type IncomingCallEvent = components['schemas']['IncomingCallEvent'];
export type AdminChecklistEvidenceRead =
    components['schemas']['AdminChecklistEvidenceRead'];
export type AdminTrainingChecklistItemRead =
    components['schemas']['AdminTrainingChecklistItemRead'];
export type ActivityLog = components['schemas']['ActivityLogRead'];
export type DemoOrgStatus = components['schemas']['DemoOrgStatus'];
export type ResetDemoOrgResponse =
    components['schemas']['ResetDemoOrgResponse'];
export type CaregiverSimThreadRead =
    components['schemas']['CaregiverSimThreadRead'];
export type CaregiverSimPersonalityRead =
    components['schemas']['CaregiverSimPersonalityRead'];
export type CaregiverSimStateResponse =
    components['schemas']['CaregiverSimStateResponse'];
export type CaregiverSimLogsResponse =
    components['schemas']['CaregiverSimLogsResponse'];
export type CaregiverSimToolCallRead =
    components['schemas']['CaregiverSimToolCallRead'];
export type CaregiverSimMemoryEventRead =
    components['schemas']['CaregiverSimMemoryEventRead'];
export type CaregiverSimScenarioRunRead =
    components['schemas']['CaregiverSimScenarioRunRead'];
export type CaregiverSimDailyRunRead =
    components['schemas']['CaregiverSimDailyRunRead'];
export type CaregiverSimDailyRunsResponse =
    components['schemas']['CaregiverSimDailyRunsResponse'];
export type CaregiverSimDailyRunReplayResponse =
    components['schemas']['CaregiverSimDailyRunReplayResponse'];
export type CaregiverSimWorldNowResponse =
    components['schemas']['CaregiverSimWorldNowResponse'];
export type CaregiverSimWorldNowEventRead =
    components['schemas']['CaregiverSimWorldNowEventRead'];
export type CaregiverSimWorldNowShiftRead =
    components['schemas']['CaregiverSimWorldNowShiftRead'];
export type CaregiverSimReplayPersonaRead =
    components['schemas']['CaregiverSimReplayPersonaRead'];
export type CaregiverSimReplayDailyEventRead =
    components['schemas']['CaregiverSimReplayDailyEventRead'];
export type CaregiverSimReplayEntryRead =
    components['schemas']['CaregiverSimReplayEntryRead'];
export type CaregiverSimTriggerDailyRunResponse =
    components['schemas']['CaregiverSimTriggerDailyRunResponse'];
export type CaregiverSimReplayFailureRead =
    components['schemas']['CaregiverSimReplayFailureRead'];
export type CaregiverSimScenarioRunRequest =
    components['schemas']['CaregiverSimScenarioRunRequest'];
export type CaregiverSimScenarioRunResponse =
    components['schemas']['CaregiverSimScenarioRunResponse'];
export type CaregiverSimResetRequest =
    components['schemas']['CaregiverSimResetRequest'];
export type CaregiverSimResetResponse =
    components['schemas']['CaregiverSimResetResponse'];
export type CaregiverSimReseedRequest =
    components['schemas']['CaregiverSimReseedRequest'];
export type CaregiverSimReseedResponse =
    components['schemas']['CaregiverSimReseedResponse'];
export type AdminSeedTrainingModeRequest =
    components['schemas']['AdminSeedTrainingModeRequest'];
export type AdminSeedTrainingModeResponse =
    components['schemas']['AdminSeedTrainingModeResponse'];
export type AdminRecommendationExplorerRequest =
    components['schemas']['AdminRecommendationExplorerRequest'];
export type AdminRecommendationExplorerResponse =
    components['schemas']['AdminRecommendationExplorerResponse'];
export type InhouseRecommendationRead =
    components['schemas']['InhouseRecommendationRead'];
export type GroupInhouseRecommendationRead =
    components['schemas']['GroupInhouseRecommendationRead'];
export type GetInhouseRecommendationsResponse =
    components['schemas']['GetInhouseRecommendationsResponse'];
export type GetGroupInhouseRecommendationsResponse =
    components['schemas']['GetGroupInhouseRecommendationsResponse'];
export type ListOutreachDismissedCaregiversResponse =
    components['schemas']['ListOutreachDismissedCaregiversResponse'];
export type ListOutreachCaregiverSelectionsResponse =
    components['schemas']['ListOutreachCaregiverSelectionsResponse'];
export type OutreachCaregiverSelectionRead =
    components['schemas']['OutreachCaregiverSelectionRead'];
export type SelectionBatchRequest =
    components['schemas']['SelectionBatchRequest'];
export type SelectionBatchItemRequest =
    components['schemas']['SelectionBatchItemRequest'];
export type SelectionBatchResponse =
    components['schemas']['SelectionBatchResponse'];
export type SelectionBatchItemResult =
    components['schemas']['SelectionBatchItemResult'];
export type StageCaregiverRequest =
    components['schemas']['StageCaregiverRequest'];
export type SkippedStagedCaregiverRead =
    components['schemas']['SkippedStagedCaregiverRead'];
export type ResetOutreachRunnerRequest =
    components['schemas']['ResetOutreachRunnerRequest'];
export type EntityFilterOptionsResponse =
    components['schemas']['EntityFilterOptionsResponse'];
export type EntityFilterDefaultsRead =
    components['schemas']['EntityFilterDefaultsRead'];
export type ShiftFilterOptionsRead =
    components['schemas']['ShiftFilterOptionsRead'];
export type RecommendationFilterNode = components['schemas']['FilterNode'];
export type RecommendationBatchCandidate =
    components['schemas']['RecommendationBatchCandidateRead'];
export type RecommendationBatchCandidatesResponse =
    components['schemas']['GetRecommendationBatchCandidatesResponse'];
export type ColumnFilterValue = components['schemas']['ColumnFilterValue'];
export type NumericRangeRead = components['schemas']['NumericRangeRead'];
export type IntRangeRead = components['schemas']['IntRangeRead'];
export type RecommendationExplorerCaregiver =
    components['schemas']['RecommendationExplorerCaregiverRead'];
export type RecommendationExplorerTerm =
    components['schemas']['RecommendationExplorerTermRead'];
export type RecommendationExplorerScoreBreakdown =
    components['schemas']['RecommendationExplorerScoreBreakdownRead'];
export type RecommendationExplorerShiftGroup =
    components['schemas']['RecommendationExplorerShiftGroupRead'];
export type RecommendationExplorerWeights =
    components['schemas']['RecommendationExplorerWeights'];
export type OrgDistributionsStaticResponse =
    components['schemas']['OrgDistributionsStaticResponse'];
export type OrgDistributionsWindowedResponse =
    components['schemas']['OrgDistributionsWindowedResponse'];
export type DistributionWindow = components['schemas']['DistributionWindow'];
export type HistogramBin = components['schemas']['HistogramBin'];
export type CategoryCount = components['schemas']['CategoryCount'];
export type CountDistribution = components['schemas']['CountDistribution'];
export type DistanceDistribution =
    components['schemas']['DistanceDistribution'];
export type ScatterPoint = components['schemas']['ScatterPoint'];
export type RadialSpreadSummary = components['schemas']['RadialSpreadSummary'];
export type SpatialClusterSummary =
    components['schemas']['SpatialClusterSummary'];
export type ClusterPopulationStats =
    components['schemas']['ClusterPopulationStats'];
export type ClusterSelectionSummary =
    components['schemas']['ClusterSelectionSummary'];
export type GeocodeCoverage = components['schemas']['GeocodeCoverage'];
export type CandidatePoolSummary =
    components['schemas']['CandidatePoolSummary'];
export type AdminVoiceCallRead = components['schemas']['AdminVoiceCallRead'];
export type AdminSyncSettingsRead =
    components['schemas']['AdminSyncSettingsRead'];
export type AdminSyncSettingsListResponse =
    components['schemas']['AdminSyncSettingsListResponse'];
export type AdminSyncSettingsUpdate =
    components['schemas']['AdminSyncSettingsUpdate'];
export type Franchise = components['schemas']['FranchiseRead'];
export type FranchiseCreate = components['schemas']['FranchiseCreate'];
export type FranchiseUpdate = components['schemas']['FranchiseUpdate'];
export type SyncEntitySettings = components['schemas']['SyncEntitySettings'];
export type SyncCapability = components['schemas']['SyncCapability'];
export type CapabilityState = components['schemas']['CapabilityState'];
export type AxisCareModificationReasonRead =
    components['schemas']['AxisCareModificationReasonRead'];
export type AxisCareWritebackReasonRead =
    components['schemas']['AxisCareWritebackReasonRead'];
export type AxisCareWritebackReasonsRead =
    components['schemas']['AxisCareWritebackReasonsRead'];
export type AxisCareModificationReasonSelection =
    components['schemas']['AxisCareModificationReasonSelection'];
export type AxisCareWritebackReasonSelections =
    components['schemas']['AxisCareWritebackReasonSelections'];
export type AxisCareModificationReasonSettingsRead =
    components['schemas']['AxisCareModificationReasonSettingsRead'];
export type AxisCareModificationReasonSettingsUpdate =
    components['schemas']['AxisCareModificationReasonSettingsUpdate'];
export type AgentConversationSummary =
    components['schemas']['AgentConversationSummary'];
export type AgentConversationListResponse =
    components['schemas']['AgentConversationListResponse'];
export type AgentConversationRead =
    components['schemas']['AgentConversationRead'];
export type AgentTurnRead = components['schemas']['AgentTurnRead'];
export type AgentItemRead = components['schemas']['AgentItemRead'];
export type AgentNoteRead = components['schemas']['AgentNoteRead'];
export type AgentNoteItemRead = components['schemas']['AgentNoteItemRead'];
export type AgentNoteEntityRead = components['schemas']['AgentNoteEntityRead'];
export type AgentNoteEntityRefRead =
    components['schemas']['AgentNoteEntityRefRead'];
export type AgentNotesForRunResponse =
    components['schemas']['AgentNotesForRunResponse'];
export type VoiceCallTransferLegRead =
    components['schemas']['VoiceCallTransferLegRead'];
export type VoiceCallPathRead = components['schemas']['VoiceCallPathRead'];
export type VoiceCallRatingCreate =
    components['schemas']['VoiceCallRatingCreate'];
export type VoiceCallRatingRead = components['schemas']['VoiceCallRatingRead'];
export type VoiceCallRatingResponse =
    components['schemas']['VoiceCallRatingResponse'];
export type VoiceCallRatingWithUserRead =
    components['schemas']['VoiceCallRatingWithUserRead'];
export type VoiceCallUserRatingResponse =
    components['schemas']['VoiceCallUserRatingResponse'];
export type VoiceCallInternalReviewsResponse =
    components['schemas']['VoiceCallInternalReviewsResponse'];
export type RatingLabel = components['schemas']['RatingLabel'];
export type RatingSource = components['schemas']['RatingSource'];
export type MessageRatingLabel = components['schemas']['MessageRatingLabel'];
export type MessageRatingSource = components['schemas']['MessageRatingSource'];
export type MessageRatingCreate = components['schemas']['MessageRatingCreate'];
export type MessageRatingRead = components['schemas']['MessageRatingRead'];
export type MessageRatingResponse =
    components['schemas']['MessageRatingResponse'];
export type AdminOutboxEntryRead =
    components['schemas']['AdminOutboxEntryRead'];
export type AdminOutboxEntryListResponse =
    components['schemas']['AdminOutboxEntryListResponse'];
export type AdminAgentNoteClassificationSummaryRead =
    components['schemas']['AdminAgentNoteClassificationSummaryRead'];
export type AdminAgentNoteAttemptRead =
    components['schemas']['AdminAgentNoteAttemptRead'];
export type AdminAgentNoteItemRead =
    components['schemas']['AdminAgentNoteItemRead'];
export type AdminAgentNoteWriteEffectRead =
    components['schemas']['AdminAgentNoteWriteEffectRead'];
export type AdminAgentNoteRead = components['schemas']['AdminAgentNoteRead'];
export type AdminAgentNoteListResponse =
    components['schemas']['AdminAgentNoteListResponse'];
export type AdminAgentActionAuditRead =
    components['schemas']['AdminAgentActionAuditRead'];
export type AdminAgentActionAuditListResponse =
    components['schemas']['AdminAgentActionAuditListResponse'];
export type AdminAgentNoteEntityRead =
    components['schemas']['AdminAgentNoteEntityRead'];
export type AdminAgentNoteEntityRefRead =
    components['schemas']['AdminAgentNoteEntityRefRead'];
export type AdminAgentNoteLinksRead =
    components['schemas']['AdminAgentNoteLinksRead'];
export type AdminAgentNoteStatusCountRead =
    components['schemas']['AdminAgentNoteStatusCountRead'];
export type AdminAgentNoteDailyCountRead =
    components['schemas']['AdminAgentNoteDailyCountRead'];
export type AdminAgentNoteHourlyCountRead =
    components['schemas']['AdminAgentNoteHourlyCountRead'];
export type AdminAgentNoteDailySourceCountRead =
    components['schemas']['AdminAgentNoteDailySourceCountRead'];
export type AdminAgentNoteOrgVolumeRead =
    components['schemas']['AdminAgentNoteOrgVolumeRead'];
export type AdminAgentNoteOrgVolumeChartsRead =
    components['schemas']['AdminAgentNoteOrgVolumeChartsRead'];
export type AdminAgentNotePreviousWindowRead =
    components['schemas']['AdminAgentNotePreviousWindowRead'];
export type AdminAgentNoteSourceCountRead =
    components['schemas']['AdminAgentNoteSourceCountRead'];
export type AdminAgentNoteDomainCountRead =
    components['schemas']['AdminAgentNoteDomainCountRead'];
export type AdminAgentNoteFunnelRead =
    components['schemas']['AdminAgentNoteFunnelRead'];
export type AdminAgentNoteLatencyStatsRead =
    components['schemas']['AdminAgentNoteLatencyStatsRead'];
export type AdminAgentNoteStatsResponse =
    components['schemas']['AdminAgentNoteStatsResponse'];
export type AdminAgentNoteOrgBreakdownRead =
    components['schemas']['AdminAgentNoteOrgBreakdownRead'];
export type AdminAgentNoteRetryResponse =
    components['schemas']['AdminAgentNoteRetryResponse'];
export type AdminAgentNoteReclassifyRequest =
    components['schemas']['AdminAgentNoteReclassifyRequest'];
export type AdminAgentNoteReclassifyResponse =
    components['schemas']['AdminAgentNoteReclassifyResponse'];
export type AdminAgentNoteReclassifyResult =
    components['schemas']['AdminAgentNoteReclassifyResult'];
export type AdminAgentNoteVolatility =
    components['schemas']['AdminAgentNoteVolatility'];
export type AdminScratchpadEvidenceRefRead =
    components['schemas']['AdminScratchpadEvidenceRefRead'];
export type AdminScratchpadScopeSummaryRead =
    components['schemas']['AdminScratchpadScopeSummaryRead'];
export type AdminScratchpadNoteSummaryRead =
    components['schemas']['AdminScratchpadNoteSummaryRead'];
export type AdminScratchpadIndexResponse =
    components['schemas']['AdminScratchpadIndexResponse'];
export type AdminScratchpadNoteReadResponse =
    components['schemas']['AdminScratchpadNoteReadResponse'];
export type AdminScratchpadRevisionRead =
    components['schemas']['AdminScratchpadRevisionRead'];
export type AdminScratchpadRevisionListResponse =
    components['schemas']['AdminScratchpadRevisionListResponse'];
export type AdminScratchpadRunNotesResponse =
    components['schemas']['AdminScratchpadRunNotesResponse'];
export type AdminScratchpadScopeRegistryResponse =
    components['schemas']['AdminScratchpadScopeRegistryResponse'];
export type AdminOutreachRead = components['schemas']['AdminOutreachRead'];
export type AdminOutreachListResponse =
    components['schemas']['AdminOutreachListResponse'];
export type AccountActivityEventRead =
    components['schemas']['AccountActivityEventRead'];
export type AccountKanbanCardRead =
    components['schemas']['AccountKanbanCardRead'];
export type AccountKanbanColumnRead =
    components['schemas']['AccountKanbanColumnRead'];
export type AccountMeetingRead = components['schemas']['AccountMeetingRead'];
export type AccountMeetingTileRead =
    components['schemas']['AccountMeetingTileRead'];
export type OrganizationKickoffBookingResponse =
    components['schemas']['OrganizationKickoffBookingResponse'];
export type AccountIntercomConversationRead =
    components['schemas']['AccountIntercomConversationRead'];
export type MeetingCalendarItemRead =
    components['schemas']['MeetingCalendarItemRead'];
export type CalendlyEventTypeMappingRead =
    components['schemas']['CalendlyEventTypeMappingRead'];
export type AccountOwnerCandidateRead =
    components['schemas']['AccountOwnerCandidateRead'];
export type AccountPageRead = components['schemas']['AccountPageRead'];
export type AccountRead = components['schemas']['AccountRead'];
export type AccountPageSummaryRead =
    components['schemas']['AccountPageSummaryRead'];
export type AccountOrganizationContextRead =
    components['schemas']['AccountOrganizationContextRead'];
export type AccountOrganizationIntegrationRead =
    components['schemas']['AccountOrganizationIntegrationRead'];
export type GetAdminAccountSummaryResponse =
    components['schemas']['GetAdminAccountSummaryResponse'];
export type AccountPageActivityRead =
    components['schemas']['AccountPageActivityRead'];
export type GetAdminAccountActivityResponse =
    components['schemas']['GetAdminAccountActivityResponse'];
export type AccountPageOnboardingRead =
    components['schemas']['AccountPageOnboardingRead'];
export type PhoebeChecklistStateRead =
    components['schemas']['PhoebeChecklistStateRead'];
export type AutomatedOnboardingGroupRead =
    components['schemas']['AutomatedOnboardingGroupRead'];
export type GetAdminAccountOnboardingResponse =
    components['schemas']['GetAdminAccountOnboardingResponse'];
export type AdminOrganizationMeetingRead =
    components['schemas']['AdminOrganizationMeetingRead'];
export type AdminOrganizationMeetingRecordingRead =
    components['schemas']['AdminOrganizationMeetingRecordingRead'];
export type GetAdminOrganizationMeetingHistoryResponse =
    components['schemas']['GetAdminOrganizationMeetingHistoryResponse'];
export type AdminOrganizationLinearTicketRead =
    components['schemas']['AdminOrganizationLinearTicketRead'];
export type AdminOrganizationA2pTicketRead =
    components['schemas']['AdminOrganizationA2pTicketRead'];
export type GetAdminOrganizationLinearTicketsResponse =
    components['schemas']['GetAdminOrganizationLinearTicketsResponse'];
export type AccountFeatureActivationRead =
    components['schemas']['AccountFeatureActivationRead'];
export type AccountFeatureActivationsResponse =
    components['schemas']['AccountFeatureActivationsResponse'];
export type AccountDeploymentPlanItemsResponse =
    components['schemas']['AccountDeploymentPlanItemsResponse'];
export type AccountCohortRead = components['schemas']['AccountCohortRead'];
export type AccountCohortCellRead =
    components['schemas']['AccountCohortCellRead'];
export type AccountCohortMemberStatusRead =
    components['schemas']['AccountCohortMemberStatusRead'];
export type AccountCohortScorecardRead =
    components['schemas']['AccountCohortScorecardRead'];
export type AccountCohortsResponse =
    components['schemas']['AccountCohortsResponse'];
export type CohortEngagementResponse =
    components['schemas']['CohortEngagementResponse'];
export type AdminSettingsRead = components['schemas']['AdminSettingsRead'];
export type AdminSettingsUpdate = components['schemas']['AdminSettingsUpdate'];
export type AccountSavedViewRead =
    components['schemas']['AccountSavedViewRead'];
export type AccountSavedViewPayload =
    components['schemas']['AccountSavedViewPayload'];
export type AccountSavedViewFilter =
    components['schemas']['AccountSavedViewFilter'];
export type AccountSavedViewDisplay =
    components['schemas']['AccountSavedViewDisplay'];
export type AccountSavedViewCreate =
    components['schemas']['AccountSavedViewCreate'];
export type AccountSavedViewUpdate =
    components['schemas']['AccountSavedViewUpdate'];
export type GetAccountSavedViewsResponse =
    components['schemas']['GetAccountSavedViewsResponse'];
export type AccountPersonRead = components['schemas']['AccountPersonRead'];
export type Persona = components['schemas']['Persona'];
export type AccountTagRead = components['schemas']['AccountTagRead'];
export type AccountTagOptionRead =
    components['schemas']['AccountTagOptionRead'];
export type GetAdminAccountTagOptionsResponse =
    components['schemas']['GetAdminAccountTagOptionsResponse'];
export type GetAdminAccountOwnerCandidatesResponse =
    components['schemas']['GetAdminAccountOwnerCandidatesResponse'];
export type DriRotationMemberRead =
    components['schemas']['DriRotationMemberRead'];
export type GetDriRotationMembersResponse =
    components['schemas']['GetDriRotationMembersResponse'];
export type GetAdminAccountMeetingsResponse =
    components['schemas']['GetAdminAccountMeetingsResponse'];
export type CreateAdminKickoffBookingRequest =
    components['schemas']['CreateAdminKickoffBookingRequest'];
export type CreateAdminKickoffBookingResponse =
    components['schemas']['CreateAdminKickoffBookingResponse'];
export type AdminKickoffBookingConflictDetail =
    components['schemas']['AdminKickoffBookingConflictDetail'];
export type AdminKickoffBookingConflictResponse =
    components['schemas']['AdminKickoffBookingConflictResponse'];
export type CreateAdminMeetingBookingLinkRequest =
    components['schemas']['CreateAdminMeetingBookingLinkRequest'];
export type CreateAdminMeetingBookingLinkResponse =
    components['schemas']['CreateAdminMeetingBookingLinkResponse'];
export type AdminMeetingBookingLinkConflictDetail =
    components['schemas']['AdminMeetingBookingLinkConflictDetail'];
export type AdminMeetingBookingLinkConflictResponse =
    components['schemas']['AdminMeetingBookingLinkConflictResponse'];
export type CreateAdminIntroCallLinkResponse =
    components['schemas']['CreateAdminIntroCallLinkResponse'];
export type CancelAdminMeetingResponse =
    components['schemas']['CancelAdminMeetingResponse'];
export type CompleteAdminKickoffResponse =
    components['schemas']['CompleteAdminKickoffResponse'];
export type CompleteAdminAccountKickoffResponse =
    components['schemas']['CompleteAdminAccountKickoffResponse'];
export type GetAdminCalendlyEventTypeMappingsResponse =
    components['schemas']['GetAdminCalendlyEventTypeMappingsResponse'];
export type UpsertAdminCalendlyEventTypeMappingRequest =
    components['schemas']['UpsertAdminCalendlyEventTypeMappingRequest'];
export type DeleteAdminCalendlyEventTypeMappingResponse =
    components['schemas']['DeleteAdminCalendlyEventTypeMappingResponse'];
export type CalendlyDirectoryEventTypeRead =
    components['schemas']['CalendlyDirectoryEventTypeRead'];
export type CalendlyDirectoryMemberRead =
    components['schemas']['CalendlyDirectoryMemberRead'];
export type GetAdminCalendlyDirectoryResponse =
    components['schemas']['GetAdminCalendlyDirectoryResponse'];
export type AutoDiscoverAdminCalendlyEventTypeMappingSkipped =
    components['schemas']['AutoDiscoverAdminCalendlyEventTypeMappingSkipped'];
export type AutoDiscoverAdminCalendlyEventTypeMappingsRequest =
    components['schemas']['AutoDiscoverAdminCalendlyEventTypeMappingsRequest'];
export type AutoDiscoverAdminCalendlyEventTypeMappingsResponse =
    components['schemas']['AutoDiscoverAdminCalendlyEventTypeMappingsResponse'];
export type GetAdminAccountResponse =
    components['schemas']['GetAdminAccountResponse'];
export type GetAdminAccountsKanbanResponse =
    components['schemas']['GetAdminAccountsKanbanResponse'];
export type UpdateAdminAccountContextRequest =
    components['schemas']['UpdateAdminAccountContextRequest'];
export type MarkAccountReviewedRequest =
    components['schemas']['MarkAccountReviewedRequest'];
export type MarkAccountReviewedResponse =
    components['schemas']['MarkAccountReviewedResponse'];
export type AdminAccountLinearTicketRead =
    components['schemas']['AdminAccountLinearTicketRead'];
export type CreateAccountLinearTicketRequest =
    components['schemas']['CreateAccountLinearTicketRequest'];
export type CreateAccountLinearTicketResponse =
    components['schemas']['CreateAccountLinearTicketResponse'];
export type AutoCalloutBoardCard =
    components['schemas']['AutoCalloutBoardCard'];
export type AutoCalloutBoardLane =
    components['schemas']['AutoCalloutBoardLane'];
export type AutoCalloutBoardShift =
    components['schemas']['AutoCalloutBoardShift'];
export type GetAutoCalloutBoardResponse =
    components['schemas']['GetAutoCalloutBoardResponse'];
export type OutreachObservabilityPulse =
    components['schemas']['OutreachObservabilityPulse'];
export type OutreachOutlierRun = components['schemas']['OutreachOutlierRun'];
export type OutreachOutlierReason = OutreachOutlierRun['reasons'][number];
export type OutreachMetricFlag = components['schemas']['OutreachMetricFlag'];
export type OutreachTrendDay = components['schemas']['OutreachTrendDay'];
export type OutreachTrendBucket = components['schemas']['OutreachTrendBucket'];
export type CaregiverHistogramBucket =
    components['schemas']['CaregiverHistogramBucket'];
export type ShiftCountDurationBucket =
    components['schemas']['ShiftCountDurationBucket'];
export type HourlyKpiBucket = components['schemas']['HourlyKpiBucket'];
export type KpiRunListItem = components['schemas']['KpiRunListItem'];
export type Mode = components['schemas']['Mode'];
export type JudgeFlagCount = components['schemas']['JudgeFlagCount'];
export type GetOutreachObservabilityResponse =
    components['schemas']['GetOutreachObservabilityResponse'];
export type StatusTile = components['schemas']['StatusTile'];
export type StatusTileLevel = StatusTile['level'];
export type ToolStatsSection = components['schemas']['ToolStatsSection'];
export type ToolStatRow = components['schemas']['ToolStatRow'];
export type ToolCallHourlyBucket =
    components['schemas']['ToolCallHourlyBucket'];
export type ToolLoopRun = components['schemas']['ToolLoopRun'];
export type ComparisonsSection = components['schemas']['ComparisonsSection'];
export type ComparisonDay = components['schemas']['ComparisonDay'];
export type ComparisonHourBucket =
    components['schemas']['ComparisonHourBucket'];
export type ComparisonDayTotals = components['schemas']['ComparisonDayTotals'];
export type JudgeStatsSection = components['schemas']['JudgeStatsSection'];
export type JudgeStatsDay = components['schemas']['JudgeStatsDay'];
export type AutoOutreachSection = components['schemas']['AutoOutreachSection'];
export type AutoOutreachPipeline =
    components['schemas']['AutoOutreachPipeline'];
export type AutoOutreachPipelineStage =
    components['schemas']['AutoOutreachPipelineStage'];
export type AutoOutreachPipelineSegment =
    components['schemas']['AutoOutreachPipelineSegment'];
export type AutoOutreachPipelineTag =
    components['schemas']['AutoOutreachPipelineTag'];
export type AutoOutreachSummary = components['schemas']['AutoOutreachSummary'];
export type AutoOutreachShiftTotals =
    components['schemas']['AutoOutreachShiftTotals'];
export type AutoOutreachTriggerCount =
    components['schemas']['AutoOutreachTriggerCount'];
export type AutoOutreachCreatorRow =
    components['schemas']['AutoOutreachCreatorRow'];
export type AutoOutreachLaneDay = components['schemas']['AutoOutreachLaneDay'];
export type AutoOutreachLaneStats =
    components['schemas']['AutoOutreachLaneStats'];
export type AutoOutreachLanes = components['schemas']['AutoOutreachLanes'];
export type AutoOutreachOrgDay = components['schemas']['AutoOutreachOrgDay'];
export type AutoOutreachOrgRow = components['schemas']['AutoOutreachOrgRow'];
export type AutoOutreachWeeklySection =
    components['schemas']['AutoOutreachWeeklySection'];
export type AutoOutreachWeek = components['schemas']['AutoOutreachWeek'];
export type AutoOutreachWeekLane =
    components['schemas']['AutoOutreachWeekLane'];
export type ModelAbSection = components['schemas']['ModelAbSection'];
export type ModelCohortStats = components['schemas']['ModelCohortStats'];
export type ModelAbTrendDay = components['schemas']['ModelAbTrendDay'];
export type JudgeFlaggedRun = components['schemas']['JudgeFlaggedRun'];
export type GetJudgeRunsResponse =
    components['schemas']['GetJudgeRunsResponse'];
export type JudgeRunListItem = components['schemas']['JudgeRunListItem'];
export type GetToolRunsResponse = components['schemas']['GetToolRunsResponse'];
export type ToolRunListItem = components['schemas']['ToolRunListItem'];
export type GetInFlightRunsResponse =
    components['schemas']['GetInFlightRunsResponse'];
export type InFlightRunListItem = components['schemas']['InFlightRunListItem'];
export type GetOutreachTvResponse =
    components['schemas']['GetOutreachTvResponse'];
export type GetSwarmWorkerObservabilityResponse =
    components['schemas']['GetSwarmWorkerObservabilityResponse'];
export type SwarmWorkerFleetSample =
    components['schemas']['SwarmWorkerFleetSample'];
export type SwarmWorkerStatus = components['schemas']['SwarmWorkerStatus'];
export type TvLatencySummaryRead =
    components['schemas']['TvLatencySummaryRead'];
export type TvHourlyBucketRead = components['schemas']['TvHourlyBucketRead'];
export type TvRecentRunRead = components['schemas']['TvRecentRunRead'];
export type TvRecsSummaryRead = components['schemas']['TvRecsSummaryRead'];
export type TvRecsBlockRead = components['schemas']['TvRecsBlockRead'];
export type TvModelCohortRead = components['schemas']['TvModelCohortRead'];
export type TvDailyMetricsSection =
    components['schemas']['TvDailyMetricsSection'];
export type TvDailyMetricsBucket =
    components['schemas']['TvDailyMetricsBucket'];
export type TvDailySwarmMetricsBucket =
    components['schemas']['TvDailySwarmMetricsBucket'];
export type OutreachRunScatterPoint =
    components['schemas']['OutreachRunScatterPoint'];
export type SchedulerFollowUpDistributionBucket =
    components['schemas']['SchedulerFollowUpDistributionBucket'];
export type RevenueAccountRead = components['schemas']['RevenueAccountRead'];
export type RevenueAccountListResponse =
    components['schemas']['RevenueAccountListResponse'];
export type RevenueAccountStatus =
    components['schemas']['RevenueAccountStatus'];
export type RevenueCustomerAccountRollupRead =
    components['schemas']['RevenueCustomerAccountRollupRead'];
export type RevenueCustomerAccountRollupListResponse =
    components['schemas']['RevenueCustomerAccountRollupListResponse'];
export type RevenueSummaryRead = components['schemas']['RevenueSummaryRead'];
export type VoipAccountRead = components['schemas']['VoipAccountRead'];
export type VoipCallWindowMetricsRead =
    components['schemas']['VoipCallWindowMetricsRead'];
export type VoipAccountListResponse =
    components['schemas']['VoipAccountListResponse'];
export type VoipAccountSourceKind =
    components['schemas']['VoipAccountSourceKind'];
export type VoipForwardingLineRead =
    components['schemas']['VoipForwardingLineRead'];
export type VoipPhoneNumberRead = components['schemas']['VoipPhoneNumberRead'];
export type VoipPhoneProvider = components['schemas']['VoipPhoneProvider'];
export type VoipCxOwner = components['schemas']['VoipCxOwner'];
export type VoipEngOwner = components['schemas']['VoipEngOwner'];
export type VoipIntegrationType = components['schemas']['VoipIntegrationType'];
export type AgreementCreate = components['schemas']['AgreementCreate'];
export type AgreementRead = components['schemas']['AgreementRead'];
export type AgreementStatus = components['schemas']['AgreementStatus'];
export type PandaDocCatalogItem = components['schemas']['PandaDocCatalogItem'];
export type PandaDocTemplateRead =
    components['schemas']['PandaDocTemplateRead'];

// Personal Access Tokens
export type AdminPersonalAccessTokenRead =
    components['schemas']['AdminPersonalAccessTokenRead'];
export type AdminPersonalAccessTokenListResponse =
    components['schemas']['AdminPersonalAccessTokenListResponse'];
export type AdminPersonalAccessTokenCreateResponse =
    components['schemas']['AdminPersonalAccessTokenCreateResponse'];

// A2P Compliance
export type A2pComplianceProfile =
    components['schemas']['A2pComplianceProfileRead'];
export type A2pComplianceProfileCreate =
    components['schemas']['A2pComplianceProfileCreate'];

// Twilio A2P Registration
export type TwilioA2pRegistration =
    components['schemas']['TwilioA2pRegistrationRead'];
export type TwilioA2pProvisioningOrganization =
    components['schemas']['TwilioA2pProvisioningOrganizationRead'];
export type TwilioA2pProvisioningDashboardResponse =
    components['schemas']['TwilioA2pProvisioningDashboardResponse'];
export type TwilioA2pRegistrationUpdate =
    components['schemas']['TwilioA2pRegistrationUpdate'];
export type TwilioA2pRegistrationMode =
    components['schemas']['TwilioA2pRegistrationMode'];

// Phone Number Port-In
export type PhoneNumberPortRequest =
    components['schemas']['PhoneNumberPortRequestRead'];
export type PhoneNumberPortRequestCreate =
    components['schemas']['PhoneNumberPortRequestCreate'];
export type PhoneNumberPortRequestStatus =
    components['schemas']['PhoneNumberPortRequestStatus'];

// Phone Number Pool Inventory
export type PhoneNumberInventoryItem =
    components['schemas']['PhoneNumberInventoryRead'];

// Data Quality
export type DataQualityIssueType =
    components['schemas']['DataQualityIssueType'];
export type DataQualityIssue = components['schemas']['DataQualityIssue'];
export type OrgDataQualitySummary =
    components['schemas']['OrgDataQualitySummary'];

// Shift Assignment Failures
export type GetShiftAssignmentFailureFilterOptionsResponse =
    components['schemas']['GetShiftAssignmentFailureFilterOptionsResponse'];
export type OrgShiftAssignmentFailureSummary =
    components['schemas']['OrgShiftAssignmentFailureSummary'];
export type ShiftAssignmentFailureRead =
    components['schemas']['ShiftAssignmentFailureRead'];
export type ShiftAssignmentFailureTrendPoint =
    components['schemas']['ShiftAssignmentFailureTrendPoint'];

export type AdminOrganization = components['schemas']['AdminOrganizationRead'];
export type Organization = components['schemas']['OrganizationRead'];
export type OrgMemberProfile =
    components['schemas']['services__api__schemas__organization__OrgMemberProfileRead'];
export type OrgMemberProfileUpdate =
    components['schemas']['services__api__schemas__organization__OrgMemberProfileUpdate'];
export type GetOrganizationMembersResponse =
    components['schemas']['GetOrganizationMembersResponse'];
export type OrganizationFeatures =
    components['schemas']['OrganizationFeatures'];
export type ActivityFeedSettings =
    components['schemas']['ActivityFeedSettings'];
export type OrganizationGeneralSettings =
    components['schemas']['OrganizationGeneralSettingsRead'];
export type DayOfWeek = components['schemas']['DayOfWeek'];
export type OrganizationVoiceSettings =
    components['schemas']['OrganizationVoiceSettingsRead'];
export type OrganizationVoiceSettingsUpdate =
    components['schemas']['OrganizationVoiceSettingsUpdate'];
export type ClientReferenceTerm = components['schemas']['ClientReferenceTerm'];
export type DistanceCalculationMode =
    components['schemas']['DistanceCalculationMode'];
export type TtsModel = components['schemas']['TtsModel'];
export type VoiceLlmModel = components['schemas']['VoiceLlmModel'];
export type CalloutSettings = components['schemas']['CalloutSettingsRead'];
export type OutreachExcludedCaregiversUpdate =
    components['schemas']['OutreachExcludedCaregiversUpdate'];
export type ShiftConfirmationSettings =
    components['schemas']['ShiftConfirmationSettingsRead'];
export type ShiftConfirmationScheduleRule =
    components['schemas']['ShiftConfirmationScheduleRule'];
export type QuietHoursWindow = components['schemas']['QuietHoursWindow'];
export type OffHoursWindow = components['schemas']['OffHoursWindow'];
export type DemoConfigUpdate = components['schemas']['DemoConfigUpdate'];
export type ShiftAssignmentType = components['schemas']['ShiftAssignmentType'];
export type RateCategory = components['schemas']['RateCategory'];
export type TemplateType = components['schemas']['TemplateType'];
export type OrganizationTemplateRead =
    components['schemas']['OrganizationTemplateRead'];

export type OrgCaregiverRecord =
    components['schemas']['OrgCaregiverRecordRead'];
export type CaregiverTag = components['schemas']['CaregiverTag'];
export type CaregiverComplianceStatus =
    components['schemas']['CaregiverComplianceStatus'];
export type OrgCaregiverRecordUpdate =
    components['schemas']['OrgCaregiverRecordUpdate'];
export type CaregiverPointSourceType =
    components['schemas']['CaregiverPointSourceType'];
export type LeaderboardRowRead = components['schemas']['LeaderboardRowRead'];
export type LeaderboardResponse = components['schemas']['LeaderboardResponse'];
export type CaregiverPointEntryRead =
    components['schemas']['CaregiverPointEntryRead'];
export type CaregiverPointEntriesResponse =
    components['schemas']['CaregiverPointEntriesResponse'];
export type CsvImportRowResult = components['schemas']['CsvImportRowResult'];
export type CsvImportResponse = components['schemas']['CsvImportResponse'];
export type CaregiverPointRuleRead =
    components['schemas']['CaregiverPointRuleRead'];
export type CaregiverPointRulesResponse =
    components['schemas']['CaregiverPointRulesResponse'];
export type ClientSecondaryContact =
    components['schemas']['ClientSecondaryContact'];
export type OrgClientRead = components['schemas']['OrgClientRead'];
export type GenderType = components['schemas']['GenderType'];
export type EmploymentType = components['schemas']['EmploymentType'];
export type AutoSendMatchMode = components['schemas']['AutoSendMatchMode'];
export type CalendarShift = components['schemas']['CalendarShiftRead'];
export type CalendarShiftListResponse =
    components['schemas']['CalendarShiftListResponse'];
export type Shift = components['schemas']['ShiftRead'];
export type ShiftNoteRead = components['schemas']['ShiftNoteRead'];
export type ShiftNotesResponse = components['schemas']['ShiftNotesResponse'];
export type ShiftStatus = components['schemas']['ShiftStatus'];
export type ShiftFrequency = components['schemas']['ShiftFrequency'];
export type ShiftConfirmation = components['schemas']['ShiftConfirmationRead'];
export type ShiftStage = components['schemas']['ShiftStage'];
export type ShiftUpdateResponse = components['schemas']['ShiftUpdateResponse'];
export type SummarizeCarePlanRequest =
    components['schemas']['SummarizeCarePlanRequest'];
export type SummarizeCarePlanResponse =
    components['schemas']['SummarizeCarePlanResponse'];
export type SampleShiftRead = components['schemas']['SampleShiftRead'];
export type SampleShiftsResponse =
    components['schemas']['SampleShiftsResponse'];
export type ShiftFillError = components['schemas']['ShiftFillError'];
export type ShiftFillFailureGroup =
    components['schemas']['ShiftFillFailureGroup'];
export type FillMultipleShiftsResponse =
    components['schemas']['FillMultipleShiftsResponse'];
export type WellSkyAssignmentReviewRead =
    components['schemas']['WellSkyAssignmentReviewRead'];
export type ShiftConfirmationExamplesResponse =
    components['schemas']['ShiftConfirmationExamplesResponse'];
export type ShiftConfirmationPreviewResponse =
    components['schemas']['ShiftConfirmationPreviewResponse'];
export type ShiftConfirmationSampleResponse =
    components['schemas']['ShiftConfirmationSampleResponse'];
export type SendPreviewSmsResponse =
    components['schemas']['SendPreviewSmsResponse'];
export type StagedMessage = components['schemas']['StagedMessageRead'];
export type StagedMessageStatus = components['schemas']['StagedMessageStatus'];
export type StagedMessageDomain = components['schemas']['StagedMessageDomain'];
export type ContactAttempt = components['schemas']['ContactAttemptRead'];
export type CaregiverResponseType =
    components['schemas']['CaregiverResponseType'];
export type ContactAttemptOutreachStatus =
    components['schemas']['ContactAttemptOutreachStatus'];

export type Outreach = components['schemas']['OutreachRead'];
export type OutreachDetailRead = components['schemas']['OutreachDetailRead'];
export type OutreachResponseCounts =
    components['schemas']['OutreachResponseCountsRead'];
export type OutreachAcceptedCaregiver =
    components['schemas']['OutreachAcceptedCaregiverRead'];
export type OutreachTemplate = components['schemas']['OutreachTemplateRead'];
export type OutreachTemplateUpdate =
    components['schemas']['OutreachTemplateUpdate'];
// Backwards compatibility alias
export type OutreachTemplateStepsUpdate = OutreachTemplateUpdate;
export type OutreachStatus = components['schemas']['OutreachStatusRead'];
export type OutreachStatusFilter =
    components['schemas']['OutreachStatusFilterRead'];
// Display status includes 'awaiting_confirmation' which is now computed by the API
export type DisplayOutreachStatus =
    components['schemas']['DisplayOutreachStatusRead'];
export type JoinedOutreachRead = components['schemas']['JoinedOutreachRead'];
export type QuietHoursHold = components['schemas']['QuietHoursHoldRead'];
export type JoinedOutreachListResponse =
    components['schemas']['JoinedOutreachListResponse'];
export type JoinedOutreachBucketRequest =
    components['schemas']['JoinedOutreachBucketRequest'];
export type JoinedOutreachBatchRequest =
    components['schemas']['JoinedOutreachBatchRequest'];
export type JoinedOutreachBatchResponse =
    components['schemas']['JoinedOutreachBatchResponse'];
export type OutreachRateMetric =
    components['schemas']['OutreachRateMetricRead'];
export type OutreachAnalytics = components['schemas']['OutreachAnalyticsRead'];
export type ClockReminderAttemptBucket =
    components['schemas']['ClockReminderAttemptBucketRead'];
export type ClockReminderReportSection =
    components['schemas']['ClockReminderReportSectionRead'];
export type ClockReminderCoordinatorCount =
    components['schemas']['ClockReminderCoordinatorCountRead'];
export type ClockReminderReport =
    components['schemas']['ClockReminderReportRead'];
export type ResourceContextRead = components['schemas']['ResourceContextRead'];

export type PlaygroundCall = components['schemas']['PlaygroundCallRead'];
export type VoiceCall = components['schemas']['VoiceCallRead'];
export type OrganizationVoiceCallDetailRead =
    components['schemas']['OrganizationVoiceCallDetailRead'];
export type AssignVoiceCallRequest =
    components['schemas']['AssignVoiceCallRequest'];
export type VoiceCallAssignmentResponse =
    components['schemas']['VoiceCallAssignmentResponse'];
export type VoiceCallAssignmentSuggestionRead =
    components['schemas']['VoiceCallAssignmentSuggestionRead'];
export type VoiceCallAssignmentSuggestionsResponse =
    components['schemas']['VoiceCallAssignmentSuggestionsResponse'];
export type CallStatus = components['schemas']['CallStatus'];
export type ContactActivityItemRead =
    components['schemas']['ContactActivityItemRead'];
export type ContactActivityResponse =
    components['schemas']['ContactActivityResponse'];

export type ContactType = components['schemas']['ContactType'];
export type DeliveryMode = components['schemas']['DeliveryMode'];

export type SmsProvider = components['schemas']['SmsProvider'];
export type ExternalSmsProviderAccount =
    components['schemas']['ExternalSmsProviderAccountRead'];

export type DataSourceAccount = components['schemas']['DataSourceAccountRead'];
export type OrganizationDataSourceAccount =
    components['schemas']['OrganizationDataSourceAccountRead'];
export type DataSourceAccountSettingsInput =
    components['schemas']['DataSourceAccountSettings'];
export type DataSourceAccountSettings =
    components['schemas']['DataSourceAccountSettings'];
export type WellSkyDataSourceAccountSettings =
    components['schemas']['WellSkyDataSourceAccountSettings'];
export type HHAExchangeDataSourceAccountSettings =
    components['schemas']['HHAExchangeDataSourceAccountSettings'];
export type WellSkySyncStrategy = components['schemas']['WellSkySyncStrategy'];
export type DataSourceType = components['schemas']['DataSourceType'];
export type SyncLog = components['schemas']['SyncLogRead'];
export type SyncQueueEntry = components['schemas']['SyncQueueEntryRead'];
export type SyncStatus = components['schemas']['SyncStatus'];
export type SyncEntityType = components['schemas']['SyncEntityType'];
export type EntitySyncState = components['schemas']['EntitySyncState'];
export type EntitySyncProgress = components['schemas']['EntitySyncProgress'];

// AxisCare types
export type AxisCareServiceRate = components['schemas']['AxisCareServiceRate'];
export type AxisCareServiceRateInfo =
    components['schemas']['AxisCareServiceRateInfo'];
export type AxisCareServiceRateInfoResponse =
    components['schemas']['AxisCareServiceRateInfoResponse'];

// HHAExchange types
export type HHAExchangeModifyReasonItem =
    components['schemas']['HHAExchangeModifyReason'];
export type HHAExchangeModifyReasonResponse =
    components['schemas']['HHAExchangeModifyReasonInfo'];

// AxisCare types
export type AxisCareModifyReasonItem =
    components['schemas']['AxisCareModifyReasonItem'];
export type AxisCareModifyReasonResponse =
    components['schemas']['AxisCareModifyReasonInfo'];

export type AcceptInvitationResponse =
    components['schemas']['AcceptInvitationResponse'];
export type UpdateOrganizationUserRequest =
    components['schemas']['UpdateOrganizationUserRequest'];
export type UpdateOrganizationUserResponse =
    components['schemas']['UpdateOrganizationUserResponse'];

// Care Coordinators
export type CareCoordinatorRead = components['schemas']['CareCoordinatorRead'];
export type LinkedUserRead = components['schemas']['LinkedUserRead'];
export type CareCoordinatorsResponse =
    components['schemas']['CareCoordinatorsResponse'];
export type LinkSuggestion = components['schemas']['LinkSuggestion'];
export type LinkSuggestionsResponse =
    components['schemas']['LinkSuggestionsResponse'];
export type LinkCareCoordinatorRequest =
    components['schemas']['LinkCareCoordinatorRequest'];

export const CAREGIVER_ROLES = [
    'rn',
    'lpn',
    'cna',
    'hha',
    'pt',
    'ot',
    'st',
    'msw',
    'cpi',
    'dsw',
    'np',
    'pca',
    'hcss',
    'acd',
    'other',
    'companion',
] as const;
assert.IsTrueType<
    AreTypesEqual<CaregiverRole, (typeof CAREGIVER_ROLES)[number]>
>();

export const SHIFT_STATUSES = [
    'open',
    'needs_coverage',
    'assigned',
    'assigned_and_confirmed',
    'pending_confirmation',
    'completed',
    'cancelled',
    'missing',
    'in_progress',
    'needs_attention',
    'not_scheduled',
    'unknown',
] as const;
assert.IsTrueType<
    AreTypesEqual<ShiftStatus, (typeof SHIFT_STATUSES)[number]>
>();

// SMS Engine Run Annotations
export type SmsEngineAnnotationSourceType =
    components['schemas']['SmsEngineAnnotationSourceType'];
export type SmsEngineRunAnnotationRead =
    components['schemas']['SmsEngineRunAnnotationRead'];
export type SmsEngineRunAnnotationCreateRequest =
    components['schemas']['SmsEngineRunAnnotationCreateRequest'];
export type SmsEngineRunAnnotationUpdateRequest =
    components['schemas']['SmsEngineRunAnnotationUpdateRequest'];

// SMS Engine Runs
export type SmsEngineRunStatus = components['schemas']['SmsEngineRunStatus'];
export type EvalRunSource = components['schemas']['EvalRunSource'];
export type EvalFamily = components['schemas']['EvalFamily-Output'];
export type EvalExecutionStatus = components['schemas']['EvalExecutionStatus'];
export type EvalExecutionBackend =
    components['schemas']['EvalExecutionBackend'];
export type EvalRunSummaryFields =
    components['schemas']['EvalRunSummaryFields'];
export type EvalRunListItem = components['schemas']['EvalRunListItem'];
export type EvalRunListResponse = components['schemas']['EvalRunListResponse'];
export type EvalRunStatsResponse =
    components['schemas']['EvalRunStatsResponse'];
export type EvalRunTrendDay = components['schemas']['EvalRunTrendDay'];
export type EvalRunTrendsResponse =
    components['schemas']['EvalRunTrendsResponse'];
export type EvalRunDetailRead = components['schemas']['EvalRunDetailRead'];
export type EvalCaseResultListItem =
    components['schemas']['EvalCaseResultListItem'];
export type EvalCaseResultDetailRead =
    components['schemas']['EvalCaseResultDetailRead'];
export type EvalCatalogRecentStats =
    components['schemas']['EvalCatalogRecentStats'];
export type EvalCatalogCase = components['schemas']['EvalCatalogCase'];
export type EvalCatalogUseCase = components['schemas']['EvalCatalogUseCase'];
export type EvalCatalogResponse = components['schemas']['EvalCatalogResponse'];
export type EvalCaseHistoryItem = components['schemas']['EvalCaseHistoryItem'];
export type EvalCaseHistoryResponse =
    components['schemas']['EvalCaseHistoryResponse'];
export type EvalRunComparisonEntry =
    components['schemas']['EvalRunComparisonEntry'];
export type EvalRunComparisonResponse =
    components['schemas']['EvalRunComparisonResponse'];
export type EvalBaselineCaseStats =
    components['schemas']['EvalBaselineCaseStats'];
export type EvalBaselineComparisonEntry =
    components['schemas']['EvalBaselineComparisonEntry'];
export type EvalBaselineComparisonVerdict =
    EvalBaselineComparisonEntry['verdict'];
export type EvalBaselineComparisonResponse =
    components['schemas']['EvalBaselineComparisonResponse'];
export type SmsEngineTriageState =
    components['schemas']['SmsEngineTriageState'];
export type SmsEngineRunListItem =
    components['schemas']['SmsEngineRunListItem'];
export type SmsEngineRunListResponse =
    components['schemas']['SmsEngineRunListResponse'];
export type SmsEngineRunStatsResponse =
    components['schemas']['SmsEngineRunStatsResponse'];
export type SmsEngineRunTriageArtifacts =
    components['schemas']['SmsEngineRunTriageArtifacts'];
export type SmsEngineRunTriageRead =
    components['schemas']['SmsEngineRunTriageRead'];
export type SmsEngineRunTriageUpdateRequest =
    components['schemas']['SmsEngineRunTriageUpdateRequest'];
export type SmsEngineRunStatusRead =
    components['schemas']['SmsEngineRunStatusRead'];
export type SmsEngineRunStatusUpdateRequest =
    components['schemas']['SmsEngineRunStatusUpdateRequest'];
export type SmsEngineRunBulkStatusUpdateRequest =
    components['schemas']['SmsEngineRunBulkStatusUpdateRequest'];
export type SmsEngineRunBulkStatusUpdateResponse =
    components['schemas']['SmsEngineRunBulkStatusUpdateResponse'];

// Agent Traces
export type AgentTraceSavedViewFilter =
    components['schemas']['AgentTraceSavedViewFilter'];
export type AgentTraceSavedViewPayload =
    components['schemas']['AgentTraceSavedViewPayload'];
export type AgentTraceSavedViewRead =
    components['schemas']['AgentTraceSavedViewRead'];
export type AgentTraceSavedViewCreate =
    components['schemas']['AgentTraceSavedViewCreate'];
export type AgentTraceSavedViewUpdate =
    components['schemas']['AgentTraceSavedViewUpdate'];
export type GetAgentTraceSavedViewsResponse =
    components['schemas']['GetAgentTraceSavedViewsResponse'];
export type AgentTraceAsideRequest =
    components['schemas']['AgentTraceAsideRequest'];
export type AgentTraceAsideResponse =
    components['schemas']['AgentTraceAsideResponse'];
export type DeleteAgentTraceSavedViewResponse =
    components['schemas']['DeleteAgentTraceSavedViewResponse'];
export type AdminAgentRunExecutionRead =
    components['schemas']['AdminAgentRunExecutionRead'];
export type AdminAgentRunRead = components['schemas']['AdminAgentRunRead'];
export type AdminAgentRunSummary =
    components['schemas']['AdminAgentRunSummary'];
export type AdminAgentModelOption =
    components['schemas']['AdminAgentModelOption'];
export type AdminAgentModelOptionsResponse =
    components['schemas']['AdminAgentModelOptionsResponse'];
export type AgentTraceChildRunListResponse =
    components['schemas']['AgentTraceChildRunListResponse'];
export type AgentTraceChildRunRead =
    components['schemas']['AgentTraceChildRunRead'];
export type AgentTraceListItem = components['schemas']['AgentTraceListItem'];
export type AgentTraceListResponse =
    components['schemas']['AgentTraceListResponse'];
export type AgentTraceDetail = components['schemas']['AgentTraceDetail'];
export type AgentTraceSummaryRead =
    components['schemas']['AgentTraceSummaryRead'];
export type AgentTraceRelatedRunRead =
    components['schemas']['AgentTraceRelatedRunRead'];
export type AgentTraceRunCursorRead =
    components['schemas']['AgentTraceRunCursorRead'];
export type AgentTraceRunListResponse =
    components['schemas']['AgentTraceRunListResponse'];
export type AgentTraceRunListRow =
    components['schemas']['AgentTraceRunListRow'];
export type AgentTraceTurnRead = components['schemas']['AgentTraceTurnRead'];
export type AgentTraceItemRead = components['schemas']['AgentTraceItemRead'];
export type AgentTraceEntityAliasRead =
    components['schemas']['AgentTraceEntityAliasRead'];
export type AgentTraceContentSpacesRead =
    components['schemas']['AgentTraceContentSpacesRead'];

// Agent Run Duration Metrics
export type AgentRunDurationTrendsResponse =
    components['schemas']['AgentRunDurationTrendsResponse'];
export type AgentRunDurationTrendBucketRead =
    components['schemas']['AgentRunDurationTrendBucketRead'];
export type AgentRunInFlightRead =
    components['schemas']['AgentRunInFlightRead'];

// Agent Court
export type AgentCourtCaseListItem =
    components['schemas']['AgentCourtCaseListItem'];
export type AgentCourtCaseListResponse =
    components['schemas']['AgentCourtCaseListResponse'];
export type AgentCourtRootCauseRead =
    components['schemas']['AgentCourtRootCauseRead'];
export type AgentCourtRootCauseListResponse =
    components['schemas']['AgentCourtRootCauseListResponse'];
export type AgentCourtCaseDetail =
    components['schemas']['AgentCourtCaseDetail'];
export type AgentCourtJusticeRunRead =
    components['schemas']['AgentCourtJusticeRunRead'];
export type AgentCourtRecommendationRead =
    components['schemas']['AgentCourtRecommendationRead'];
export type AgentCourtRecommendationGroupRead =
    components['schemas']['AgentCourtRecommendationGroupRead'];
export type AgentCourtUserFeedbackRead =
    components['schemas']['AgentCourtUserFeedbackRead'];
export type AgentCourtContextReviewedRead =
    components['schemas']['AgentCourtContextReviewedRead'];
export type AgentCourtCaseSourceLink =
    components['schemas']['AgentCourtCaseSourceLink'];
export type PlaybookOptionRead = components['schemas']['PlaybookOptionRead'];
export type PlaybookRecommendationReviewRead =
    components['schemas']['PlaybookRecommendationReviewRead'];
export type ApprovePlaybookRecommendationRequest =
    components['schemas']['ApprovePlaybookRecommendationRequest'];
export type RejectRecommendationRequest =
    components['schemas']['RejectRecommendationRequest'];
export type RecommendationReviewResultRead =
    components['schemas']['RecommendationReviewResultRead'];
// Calls (Core meeting call recordings)
export type CallListItemRead = components['schemas']['CallListItemRead'];
export type GetCallsResponse = components['schemas']['GetCallsResponse'];
export type CallDetailRead = components['schemas']['CallDetailRead'];
export type CallTagRead = components['schemas']['CallTagRead'];
export type CallTagsResponse = components['schemas']['CallTagsResponse'];
export type AddCallTagRequest = components['schemas']['AddCallTagRequest'];
export type SetCallTagsRequest = components['schemas']['SetCallTagsRequest'];

// Activity feed
export type ActivityEvent = components['schemas']['ActivityEventRead'];
export type ActivityEventUser = components['schemas']['ActivityEventUserRead'];
export type ActivityEventAssignee =
    components['schemas']['ActivityEventAssigneeRead'];
export type GetActivityEventAssigneesResponse =
    components['schemas']['GetActivityEventAssigneesResponse'];
export type UpdateActivityEventAssignmentsRequest =
    components['schemas']['UpdateActivityEventAssignmentsRequest'];
export type UpdateActivityEventAssignmentsResponse =
    components['schemas']['UpdateActivityEventAssignmentsResponse'];
export type ActivityEventsExport =
    components['schemas']['ActivityEventsExportRead'];
export type ActivityEventPriority =
    components['schemas']['ActivityEventPriority'];
export type ActivityEventCounts =
    components['schemas']['ActivityEventCountsResponse'];
export type ActivityEventKindCount =
    components['schemas']['ActivityEventKindCount'];
export type ActivityEventKindCounts =
    components['schemas']['ActivityEventKindCountsResponse'];
export type ActivityEventTransferDestinationCount =
    components['schemas']['ActivityEventTransferDestinationCount'];
export type GetActivityEventsResponse =
    components['schemas']['GetActivityEventsResponse'];
export type MarkAddressedRequest =
    components['schemas']['MarkAddressedRequest'];
export type VoiceCallNote = components['schemas']['VoiceCallNoteRead'];
export type GetVoiceCallNotesResponse =
    components['schemas']['GetVoiceCallNotesResponse'];

// Voice QA
export type VoiceQAListItem = components['schemas']['VoiceQAListItem'];
export type VoiceQAListResponse = components['schemas']['VoiceQAListResponse'];
export type VoiceQAStatsResponse =
    components['schemas']['VoiceQAStatsResponse'];
export type VoiceQADetailRead = components['schemas']['VoiceQADetailRead'];
export type VoiceQAOwnerAssignmentRequest =
    components['schemas']['VoiceQAOwnerAssignmentRequest'];
export type VoiceQAOwnerAssignmentResponse =
    components['schemas']['VoiceQAOwnerAssignmentResponse'];
export type VoiceCallAuditStatus =
    components['schemas']['VoiceCallAuditStatus'];
export type VoiceCallAuditDisposition =
    components['schemas']['VoiceCallAuditDisposition'];
export type VoiceCallAuditRunRead =
    components['schemas']['VoiceCallAuditRunRead'];
export type VoiceCallAuditFindingRead =
    components['schemas']['VoiceCallAuditFindingRead'];
export type VoiceCallAuditOutcomeTopicRead =
    components['schemas']['VoiceCallAuditOutcomeTopicRead'];
export type VoiceCallAuditActionRead =
    components['schemas']['VoiceCallAuditActionRead'];
export type VoiceCallNoteRunRead =
    components['schemas']['VoiceCallNoteRunRead'];
export type VoiceCallMemoryFragmentRead =
    components['schemas']['VoiceCallMemoryFragmentRead'];
export type VoiceQAReceptionistItemRead =
    components['schemas']['VoiceQAReceptionistItemRead'];
export type VoiceQATranscriptSotAuditRead =
    components['schemas']['VoiceQATranscriptSotAuditRead'];
export type VoiceQATranscriptSotTurnRead =
    components['schemas']['VoiceCallTranscriptSotTurnRead'];
export type VoiceQaSavedViewFilter =
    components['schemas']['VoiceQaSavedViewFilter'];
export type VoiceQaSavedViewPayload =
    components['schemas']['VoiceQaSavedViewPayload'];
export type VoiceQaSavedViewRead =
    components['schemas']['VoiceQaSavedViewRead'];
export type VoiceQaSavedViewCreate =
    components['schemas']['VoiceQaSavedViewCreate'];
export type VoiceQaSavedViewUpdate =
    components['schemas']['VoiceQaSavedViewUpdate'];
export type GetVoiceQaSavedViewsResponse =
    components['schemas']['GetVoiceQaSavedViewsResponse'];
export type DeleteVoiceQaSavedViewResponse =
    components['schemas']['DeleteVoiceQaSavedViewResponse'];

// Voice QA shadow comparison
export type VoiceQAShadowStatsResponse =
    components['schemas']['VoiceQAShadowStatsResponse'];
export type VoiceQAShadowConfusionCell =
    components['schemas']['VoiceQAShadowConfusionCell'];
export type VoiceQAShadowLatencyRead =
    components['schemas']['VoiceQAShadowLatencyRead'];
export type VoiceQAShadowPairRead =
    components['schemas']['VoiceQAShadowPairRead'];
export type VoiceQAShadowAdjudicationCountRead =
    components['schemas']['VoiceQAShadowAdjudicationCountRead'];
export type VoiceQAShadowModelStatsRead =
    components['schemas']['VoiceQAShadowModelStatsRead'];
export type VoiceQAShadowCostRead =
    components['schemas']['VoiceQAShadowCostRead'];

export type QaVoiceAgentSettingsResponse =
    components['schemas']['QaVoiceAgentSettingsResponse'];
export type QaVoiceAgentSettingsOverrideRead =
    components['schemas']['QaVoiceAgentSettingsOverrideRead'];

export type TwilioA2pRegistrationDetail =
    components['schemas']['TwilioA2pRegistrationDetailRead'];
export type TextingCanaryAttempt =
    components['schemas']['TextingCanaryAttemptRead'];
export type QaVoiceAgentSettingsResolvedRead =
    components['schemas']['QaVoiceAgentSettingsResolvedRead'];
export type QaVoiceAgentSettingsUpdate =
    components['schemas']['QaVoiceAgentSettingsUpdate'];
export type QaVoiceAgentShadowLane =
    components['schemas']['QaVoiceAgentShadowLane'];
export type QaVoiceAgentShadowLaneUpdate =
    components['schemas']['QaVoiceAgentShadowLaneUpdate'];
export type AuthorizationsLeaderboardRow =
    components['schemas']['AuthorizationsLeaderboardRow'];
export type AuthorizationsLeaderboardResponse =
    components['schemas']['AuthorizationsLeaderboardResponse'];
export type AccountHealthDaily =
    components['schemas']['AccountHealthDailyRead'];
export type AccountHealthPooled =
    components['schemas']['AccountHealthPooledRead'];
export type SmsResponseByType = components['schemas']['SmsResponseByTypeRead'];
export type AccountHealthMetricsResponse =
    components['schemas']['GetAccountHealthMetricsResponse'];
export type AcrossOrgsHealthRow = components['schemas']['AcrossOrgsHealthRow'];
export type AcrossOrgsHealthMetricsResponse =
    components['schemas']['GetAcrossOrgsHealthMetricsResponse'];
export type QuickHealthDaily = components['schemas']['QuickHealthDailyRead'];
export type QuickHealthOpenAlert =
    components['schemas']['QuickHealthOpenAlertRead'];
export type OrganizationQuickHealth =
    components['schemas']['OrganizationQuickHealthRead'];
export type QuickHealthMetricsResponse =
    components['schemas']['GetQuickHealthMetricsResponse'];
export type VoipHealthDaily = components['schemas']['VoipHealthDailyRead'];
export type VoipHealthPooled = components['schemas']['VoipHealthPooledRead'];
export type VoipTransferDestination =
    components['schemas']['VoipTransferDestinationRead'];
export type VoipHealthMetricsResponse =
    components['schemas']['GetVoipHealthMetricsResponse'];
export type OrganizationReport =
    components['schemas']['OrganizationReportRead'];

export type OrganizationReportComparison =
    components['schemas']['OrganizationReportComparison'];
export type RoiMetricsDaily = components['schemas']['RoiMetricsDailyRead'];
export type RoiMetricsTotals = components['schemas']['RoiMetricsTotalsRead'];
export type OrganizationRoiMetrics =
    components['schemas']['OrganizationRoiMetricsRead'];
export type EngagementMetricsDaily =
    components['schemas']['EngagementMetricsDailyRead'];
export type OrganizationEngagementMetrics =
    components['schemas']['OrganizationEngagementMetricsRead'];
export type AgentAnalysisFlowCell =
    components['schemas']['AgentAnalysisFlowCellRead'];
export type AgentAnalysisGapItem =
    components['schemas']['AgentAnalysisGapItemRead'];
export type AgentAnalysisClaimExample =
    components['schemas']['AgentAnalysisClaimExampleRead'];
export type AgentAnalysisSurface =
    components['schemas']['AgentAnalysisSurfaceRead'];
export type OrganizationAgentAnalysis =
    components['schemas']['GetAgentAnalysisResponse'];
export type GlobalAgentAnalysis =
    components['schemas']['GetGlobalAgentAnalysisResponse'];
export type OrganizationUnmetDemandItemRead =
    components['schemas']['OrganizationUnmetDemandItemRead'];
export type GetOrganizationUnmetDemandResponse =
    components['schemas']['GetOrganizationUnmetDemandResponse'];
export type EpisodeClassificationCoverage =
    components['schemas']['EpisodeClassificationCoverageRead'];
export type EpisodeRequestItems =
    components['schemas']['EpisodeRequestItemsRead'];
export type RecentRequests = components['schemas']['RecentRequestsRead'];
export type RecentRequest = components['schemas']['RecentRequestRead'];
export type RequestItemDimensionCount =
    components['schemas']['RequestItemDimensionCountRead'];
export type RequestItemFlowCell =
    components['schemas']['RequestItemFlowCellRead'];
export type RequestWorkflowEdge =
    components['schemas']['RequestWorkflowEdgeRead'];
export type ShortLinkResolution = components['schemas']['ShortLinkResolution'];
export type OutboundTranslationPreview =
    components['schemas']['TranslatePreviewResponse'];
export type ClickToCallCreate = components['schemas']['ClickToCallCreate'];
export type ClickToCallRead = components['schemas']['ClickToCallRead'];
export type PhoneSystemForwardingValidationRead =
    components['schemas']['PhoneSystemForwardingValidationRead'];
export type PhoneSystemForwardingValidationActionResponse =
    components['schemas']['PhoneSystemForwardingValidationActionResponse'];
export type Alive5APIAccountRead =
    components['schemas']['Alive5APIAccountRead'];
export type Alive5ChannelRead = components['schemas']['Alive5ChannelRead'];
export type GetAlive5StatusResponse =
    components['schemas']['GetAlive5StatusResponse'];
export type CallHandlingSettings =
    components['schemas']['CallHandlingSettings'];
export type CallHandlingSchedule =
    components['schemas']['CallHandlingSchedule'];
export type CallHandlingScheduleConfig =
    components['schemas']['CallHandlingScheduleConfig'];
export type TrainingOverviewResponse =
    components['schemas']['TrainingOverviewResponse'];
export type TrainingRunRead = components['schemas']['TrainingRunRead'];
export type TrainingNeedsReviewResponse =
    components['schemas']['TrainingNeedsReviewResponse'];
export type TrainingNeedsReviewRunRead =
    components['schemas']['TrainingNeedsReviewRunRead'];
export type TrainingFeedChannel = components['schemas']['TrainingFeedChannel'];
export type AgentRunFeedbackRead =
    components['schemas']['AgentRunFeedbackRead'];
export type AgentRunFeedbackUpdateRequest =
    components['schemas']['AgentRunFeedbackUpdateRequest'];
export type AgentRunDebriefRead = components['schemas']['AgentRunDebriefRead'];
export type AgentRunDebriefDecisionRead =
    components['schemas']['AgentRunDebriefDecisionRead'];
export type AgentRunDebriefDecisionVerdict =
    components['schemas']['AgentRunDebriefDecisionVerdict'];
export type AgentApprovalMode = components['schemas']['AgentApprovalMode'];
export type AgentRunApprovalModeUpdateResponse =
    components['schemas']['AgentRunApprovalModeUpdateResponse'];
export type SmsRunRatingRead = components['schemas']['SmsRunRatingRead'];
export type SmsRunRatingsResponse =
    components['schemas']['SmsRunRatingsResponse'];
export type AgencyContextTopicRead =
    components['schemas']['AgencyContextTopicRead'];
export type AgencyContextItemRead =
    components['schemas']['AgencyContextItemRead'];
export type AgencyContextResponse =
    components['schemas']['AgencyContextResponse'];
export type AgencyContextSaveRequest =
    components['schemas']['AgencyContextSaveRequest'];
export type AdminAgentAutomationRunRead =
    components['schemas']['AdminAgentAutomationRunRead'];
export type AdminAgentAutomationRunListResponse =
    components['schemas']['AdminAgentAutomationRunListResponse'];
export type AdminAgentAutomationMemoryRead =
    components['schemas']['AdminAgentAutomationMemoryRead'];
export type AdminAgentAutomationMemoryListResponse =
    components['schemas']['AdminAgentAutomationMemoryListResponse'];
export type AdminAgentAutomationAttentionResponse =
    components['schemas']['AdminAgentAutomationAttentionResponse'];
export type AdminAgentAutomationDeadLetterDispatchRead =
    components['schemas']['AdminAgentAutomationDeadLetterDispatchRead'];
export type AdminAgentAutomationFailingScheduleRead =
    components['schemas']['AdminAgentAutomationFailingScheduleRead'];

export const ORGANIZATION_EVENT_TYPES = [
    'message_created',
    'conversation_updated',
    'pending_message_updated',
    'contact_attempt_updated',
    'shifts_updated',
    'shift_confirmation_updated',
    'conversations_unread_updated',
    'notification_info',
    'notification_success',
    'notification_warning',
    'notification_error',
] as const;
export type OrganizationEventType = (typeof ORGANIZATION_EVENT_TYPES)[number];

export type AmdShadowSummary = components['schemas']['AmdShadowSummaryRead'];
export type AmdShadowCall = components['schemas']['AmdShadowCallRead'];
export type AmdShadowCandidateSummary =
    components['schemas']['AmdShadowCandidateSummaryRead'];
export type AmdLiveSummary = components['schemas']['AmdLiveSummaryRead'];
export type AmdLiveCall = components['schemas']['AmdLiveCallRead'];

export type WorkforceAvailabilityResponse =
    components['schemas']['WorkforceAvailabilityResponse'];
export type WorkforceAvailabilityRow =
    components['schemas']['WorkforceAvailabilityRow'];
export type WorkforceAvailabilityCell =
    components['schemas']['AvailabilityCellRead'];

export type WorkforceMapResponse =
    components['schemas']['WorkforceMapResponse'];
export type WorkforceMapPoint = components['schemas']['WorkforceMapPoint'];
export type WorkforceMapClientDensityCell =
    components['schemas']['WorkforceMapClientDensityCell'];
export type WorkforceMapClientDensityResponse =
    components['schemas']['WorkforceMapClientDensityResponse'];

export type SchedulerAssessment =
    components['schemas']['SchedulerAssessmentRead'];
export type GetSchedulerAssessmentsResponse =
    components['schemas']['GetSchedulerAssessmentsResponse'];
export type ReviewSchedulerAssessmentRequest =
    components['schemas']['ReviewSchedulerAssessmentRequest'];

export type SchedulerSelfIdentificationRead =
    components['schemas']['SchedulerSelfIdentificationRead'];
export type SchedulerSelfIdentificationSubmit =
    components['schemas']['SchedulerSelfIdentificationSubmit'];
export type SchedulerActivityEntry =
    components['schemas']['SchedulerActivityEntry'];
export type SchedulerActivityReport =
    components['schemas']['GetSchedulerActivityReportResponse'];
export type TrainingCoverageResponse =
    components['schemas']['TrainingCoverageResponse'];
export type CoverageCell = components['schemas']['CoverageCellRead'];
export type CoverageGapCaregiver = components['schemas']['GapCaregiverRead'];
export type CaregiverGapPage = components['schemas']['GapPageResponse'];

export type CoverageMetrics = components['schemas']['CoverageMetricsRead'];
export type CoverageTrendPoint =
    components['schemas']['CoverageTrendPointRead'];
export type PhoebeActivityHighlight =
    components['schemas']['PhoebeActivityHighlightRead'];
export type PhoebeAction = components['schemas']['PhoebeActionRead'];
export type SettingsAuditEntry =
    components['schemas']['SettingsAuditEntryRead'];
export type SettingsAuditListResponse =
    components['schemas']['SettingsAuditListResponse'];

// Voice config branches (A/B testing of per-organization voice agent config)
// and the per-call agent response latency surfaces that compare them.
export type TtsService = components['schemas']['TtsService'];
export type CartesiaTtsEmotion = components['schemas']['CartesiaTtsEmotion'];
export type VoiceCompletionPriority =
    components['schemas']['VoiceCompletionPriority'];
export type VoiceLlmRuntimeConfig =
    components['schemas']['VoiceLlmRuntimeConfig'];
export type VoiceLlmEndpointConfig =
    components['schemas']['VoiceLlmEndpointConfig'];
export type VoiceSpeechRuntimeConfig =
    components['schemas']['VoiceSpeechRuntimeConfig'];
export type VoiceConfigOrganizationMatrix =
    components['schemas']['VoiceConfigOrganizationMatrixRead'];
export type VoiceConfigOrganizationMatrixRow =
    components['schemas']['VoiceConfigOrganizationMatrixRowRead'];
export type OrganizationVoiceConfig =
    components['schemas']['OrganizationVoiceConfigRead'];
export type VoiceConfigEffective =
    components['schemas']['VoiceConfigEffectiveRead'];
export type VoiceConfigValues = components['schemas']['VoiceConfigValuesRead'];
export type VoiceConfigEffectiveField =
    components['schemas']['VoiceConfigEffectiveFieldRead'];
export type VoiceConfigBranch = components['schemas']['VoiceConfigBranchRead'];
export type VoiceConfigBranchStatus =
    components['schemas']['VoiceConfigBranchStatus'];
export type VoiceConfigBranchOverrides =
    components['schemas']['VoiceConfigBranchOverrides'];
export type VoiceConfigBranchDiff =
    components['schemas']['VoiceConfigBranchDiffRead'];
export type VoiceConfigBranchDifference =
    components['schemas']['VoiceConfigBranchDifferenceRead'];
export type VoiceConfigMetricRow =
    components['schemas']['VoiceConfigMetricRowRead'];
export type VoiceCallLatency = components['schemas']['VoiceCallLatencyRead'];
export type VoiceCallLatencyTurn =
    components['schemas']['AgentResponseLatencyTurn'];
export type VoiceCallLatencySummary =
    components['schemas']['AgentResponseLatencySummary'];
export type VoiceCallLatencyTimeseries =
    components['schemas']['VoiceCallLatencyTimeseriesRead'];
export type VoiceCallLatencySeries =
    components['schemas']['VoiceCallLatencySeriesRead'];
export type VoiceCallLatencyBucket =
    components['schemas']['VoiceCallLatencyBucketRead'];

export type CaregiverPreferenceSettings =
    components['schemas']['CaregiverPreferenceSettingsRead'];
export type CaregiverPreferenceSettingsPatch =
    components['schemas']['CaregiverPreferenceSettingsPatch'];
export type CaregiverPreferenceCoverage =
    components['schemas']['CaregiverPreferenceCoverageRead'];
export type CaregiverSupplyArea =
    components['schemas']['CaregiverSupplyAreaRead'];
export type CaregiverSupplySummary =
    components['schemas']['CaregiverSupplySummaryRead'];
export type CaregiverPreferenceSurveyCreate =
    components['schemas']['CaregiverPreferenceSurveyCreate'];
export type CaregiverPreferenceSurveyCreateResult =
    components['schemas']['CaregiverPreferenceSurveyCreateRead'];
export type CaregiverPreferenceSurvey =
    components['schemas']['CaregiverPreferenceSurveyRead'];
export type CaregiverPreferenceSession =
    components['schemas']['CaregiverPreferenceSessionRead'];
export type CaregiverPreferenceSurveyCall =
    components['schemas']['CaregiverPreferenceSurveyCallRead'];
export type CaregiverPreferenceSmsRemindersCreate =
    components['schemas']['CaregiverPreferenceSmsRemindersCreate'];
export type CaregiverPreferenceSmsRemindersResult =
    components['schemas']['CaregiverPreferenceSmsRemindersRead'];
export type CaregiverSchedulingProfile =
    components['schemas']['SchedulingProfileView'];

export type SandboxFileRead = components['schemas']['SandboxFileRead'];
export type SandboxRunRead = components['schemas']['SandboxRunRead'];
export type SandboxOrganizationRead =
    components['schemas']['SandboxOrganizationRead'];
export type SandboxOrganizationListResponse =
    components['schemas']['SandboxOrganizationListResponse'];
export type SandboxOrganizationDetailResponse =
    components['schemas']['SandboxOrganizationDetailResponse'];
export type SandboxFilePreviewRead =
    components['schemas']['SandboxFilePreviewRead'];

export type ScenarioApprovalRuleItem =
    components['schemas']['ScenarioApprovalRuleItem'];
export type ScenarioApprovalRulesResponse =
    components['schemas']['ScenarioApprovalRulesResponse'];
export type ScenarioApprovalRulesUpdate =
    components['schemas']['ScenarioApprovalRulesUpdate'];
